import { SearchOutlined, UserOutlined } from "@ant-design/icons";
import { CONCERN_GROUP_CATEGORIES } from "@tams-app/concerns";
import {
	Button,
	DatePicker,
	Input,
	Select,
	Space,
	Table,
	Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminPage } from "../../components/AdminPage";
import { formatAdminDate } from "../../lib/format";
import { getAccessToken } from "../../lib/staff";
import { getTrpcClient } from "../../lib/trpc";
import { tamsThemeColors } from "../../theme";

type AccountStatus = "active" | "deletion_pending";

type UserRow = {
	accountStatus: AccountStatus;
	concerns: string[];
	createdAt: string | null;
	displayName: string | null;
	email: string | null;
	emailVisible: boolean;
	id: string;
	reportCount: number;
};

type AppliedFilters = {
	accountStatus?: AccountStatus;
	concern?: string;
	createdFrom?: string;
	createdTo?: string;
	displayName?: string;
	hasReports?: boolean;
};

const ACCOUNT_STATUS_LABEL: Record<AccountStatus, string> = {
	active: "有効",
	deletion_pending: "削除申請中",
};

export function UsersListPage() {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [rows, setRows] = useState<UserRow[]>([]);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);

	const [draftDisplayName, setDraftDisplayName] = useState("");
	const [draftRange, setDraftRange] = useState<
		[Dayjs | null, Dayjs | null] | null
	>(null);
	const [draftConcern, setDraftConcern] = useState<string | undefined>();
	const [draftHasReports, setDraftHasReports] = useState<boolean | undefined>();
	const [draftAccountStatus, setDraftAccountStatus] = useState<
		AccountStatus | undefined
	>();
	const [applied, setApplied] = useState<AppliedFilters>({});

	const fetchUsers = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const accessToken = await getAccessToken();
			if (!accessToken) {
				throw new Error("サインインが必要です。");
			}
			const result = await getTrpcClient(accessToken).admin.listUsers.query({
				page,
				pageSize,
				...applied,
			});
			setRows(result.items);
			setTotal(result.total);
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: "ユーザー一覧の取得に失敗しました。",
			);
		} finally {
			setLoading(false);
		}
	}, [applied, page, pageSize]);

	useEffect(() => {
		void fetchUsers();
	}, [fetchUsers]);

	const onSearch = () => {
		const next: AppliedFilters = {};
		const name = draftDisplayName.trim();
		if (name) next.displayName = name;
		if (draftRange?.[0]) next.createdFrom = draftRange[0].format("YYYY-MM-DD");
		if (draftRange?.[1]) next.createdTo = draftRange[1].format("YYYY-MM-DD");
		if (draftConcern) next.concern = draftConcern;
		if (draftHasReports !== undefined) next.hasReports = draftHasReports;
		if (draftAccountStatus) next.accountStatus = draftAccountStatus;
		setPage(1);
		setApplied(next);
	};

	const onReset = () => {
		setDraftDisplayName("");
		setDraftRange(null);
		setDraftConcern(undefined);
		setDraftHasReports(undefined);
		setDraftAccountStatus(undefined);
		setPage(1);
		setApplied({});
	};

	const showEmailColumn = rows.some((row) => row.emailVisible);

	const columns = useMemo<ColumnsType<UserRow>>(() => {
		const cols: ColumnsType<UserRow> = [
			{
				title: "ユーザーID",
				dataIndex: "displayName",
				render: (value: string | null) => value || "—",
			},
		];
		if (showEmailColumn) {
			cols.push({
				title: "メール",
				dataIndex: "email",
				render: (value: string | null) => value || "—",
			});
		}
		cols.push(
			{
				title: "登録日",
				dataIndex: "createdAt",
				width: 120,
				render: (value: string | null) => formatAdminDate(value),
			},
			{
				title: "お悩みカテゴリ",
				dataIndex: "concerns",
				render: (value: string[]) =>
					value.length > 0 ? value.join(" / ") : "—",
			},
			{
				title: "アカウント状態",
				dataIndex: "accountStatus",
				width: 140,
				render: (value: AccountStatus) => ACCOUNT_STATUS_LABEL[value],
			},
			{
				title: "操作",
				key: "actions",
				width: 96,
				render: () => (
					<Button
						size="small"
						disabled
						style={{
							borderColor: tamsThemeColors.primary,
							color: tamsThemeColors.primary,
						}}
					>
						詳細
					</Button>
				),
			},
		);
		return cols;
	}, [showEmailColumn]);

	return (
		<AdminPage
			title="ユーザー管理"
			subtitle="ユーザー一覧・詳細"
			filters={
				<>
					<Input
						allowClear
						prefix={<UserOutlined style={{ color: tamsThemeColors.muted }} />}
						placeholder="ユーザーIDを入力"
						value={draftDisplayName}
						onChange={(event) => setDraftDisplayName(event.target.value)}
						onPressEnter={onSearch}
						style={{ width: 200 }}
					/>
					<DatePicker.RangePicker
						value={draftRange}
						onChange={(value) =>
							setDraftRange(value as [Dayjs | null, Dayjs | null] | null)
						}
						style={{ width: 260 }}
					/>
					<Select
						allowClear
						placeholder="お悩みカテゴリ"
						value={draftConcern}
						onChange={setDraftConcern}
						style={{ width: 180 }}
						options={(CONCERN_GROUP_CATEGORIES as readonly string[]).map(
							(value) => ({
								value,
								label: value,
							}),
						)}
					/>
					<Select
						allowClear
						placeholder="報告履歴"
						value={
							draftHasReports === undefined
								? undefined
								: draftHasReports
									? "yes"
									: "no"
						}
						onChange={(value) => {
							if (value === "yes") setDraftHasReports(true);
							else if (value === "no") setDraftHasReports(false);
							else setDraftHasReports(undefined);
						}}
						style={{ width: 140 }}
						options={[
							{ value: "yes", label: "あり" },
							{ value: "no", label: "なし" },
						]}
					/>
					<Select
						allowClear
						placeholder="アカウント状態"
						value={draftAccountStatus}
						onChange={setDraftAccountStatus}
						style={{ width: 160 }}
						options={[
							{ value: "active", label: "有効" },
							{ value: "deletion_pending", label: "削除申請中" },
						]}
					/>
					<Space>
						<Button onClick={onReset}>リセット</Button>
						<Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>
							検索
						</Button>
					</Space>
				</>
			}
			toolbar={
				<span className="admin-page-result-count">
					検索結果：{total.toLocaleString("ja-JP")}件
				</span>
			}
		>
			{error ? (
				<div style={{ padding: 24 }}>
					<Typography.Text type="danger">{error}</Typography.Text>
				</div>
			) : (
				<Table
					rowKey="id"
					size="middle"
					loading={loading}
					dataSource={rows}
					sticky
					scroll={{ x: true }}
					pagination={{
						current: page,
						pageSize,
						total,
						showSizeChanger: true,
						showTotal: (count) => `${count} 件`,
						onChange: (nextPage, nextSize) => {
							setPage(nextPage);
							setPageSize(nextSize);
						},
					}}
					columns={columns}
				/>
			)}
		</AdminPage>
	);
}
